// Follow camera. Owned by the runtime; the module never positions it.
// The visible rectangle is kept inside the server's culling rectangle for this player
// (see Room.viewRect) so nothing pops in at the edge of the screen.

export interface CameraTarget {
  x: number;
  y: number;
  r: number;
}

/** World units the server replicates around a player at scale 1 (must match Room.viewRect). */
const VIEW_W = 1920;
const VIEW_H = 1200;

export class Camera {
  x = 0;
  y = 0;
  /** Screen pixels per world unit. */
  zoom = 1;
  /** Extra magnification on top of the fit-to-view zoom; previews use it to show a closer slice. */
  magnify = 1;
  private initialised = false;

  /**
   * Ease toward the target. `dt` in seconds; smoothing is dt-scaled so phones at 30 fps and
   * desktops at 144 fps converge at the same speed.
   */
  follow(target: CameraTarget | undefined, w: number, h: number, dt: number, worldW: number, worldH: number): void {
    const scale = target ? Math.min(6, Math.max(1, Math.sqrt(target.r / 25))) : 2;
    const zoom = Math.max(w / (VIEW_W * scale), h / (VIEW_H * scale)) * this.magnify;
    const tx = target ? target.x : worldW / 2;
    const ty = target ? target.y : worldH / 2;
    if (!this.initialised) {
      this.x = tx;
      this.y = ty;
      this.zoom = zoom;
      this.initialised = true;
      return;
    }
    const k = 1 - Math.exp(-dt * 8);
    this.x += (tx - this.x) * k;
    this.y += (ty - this.y) * k;
    this.zoom += (zoom - this.zoom) * (1 - Math.exp(-dt * 3));
  }

  /** Set the canvas transform so world units map to CSS pixels, scaled by `dpr` to device pixels. */
  apply(ctx: CanvasRenderingContext2D, w: number, h: number, dpr = 1): void {
    const z = this.zoom * dpr;
    ctx.setTransform(z, 0, 0, z, (w / 2 - this.x * this.zoom) * dpr, (h / 2 - this.y * this.zoom) * dpr);
  }

  toWorld(sx: number, sy: number, w: number, h: number): { x: number; y: number } {
    return { x: (sx - w / 2) / this.zoom + this.x, y: (sy - h / 2) / this.zoom + this.y };
  }

  /** Visible world rectangle. */
  rect(w: number, h: number): { x0: number; y0: number; x1: number; y1: number } {
    const hw = w / 2 / this.zoom;
    const hh = h / 2 / this.zoom;
    return { x0: this.x - hw, y0: this.y - hh, x1: this.x + hw, y1: this.y + hh };
  }
}
